import { createTheme } from '@mui/material';
import { BRAND_PRIMARY, BRAND_PRIMARY_DARK, BRAND_PRIMARY_LIGHT } from './theme/brandColors';

/**
 * getTheme(mode) — light / dark モードに応じた MUI テーマを生成
 *
 * 既存の light テーマを維持しつつ、 dark モードでも MUI コンポーネントが
 * 自動的に dark に切り替わるよう palette.mode を出し分け。
 *
 * ★ iter 29 (2026-05-14、 Phase 8): brand color を src/theme/brandColors.js で単一定義化。
 *    新規 component は theme.palette.primary.main または brandColors の import を使うこと。
 *
 * 注: アプリ内で hardcode された色 (#fff, #6b7280 等) は dark に追従しない
 *      ため、 個別コンポーネントで theme.palette.mode を見て切替えること。
 */
export const getTheme = (mode = 'light') => {
  const isDark = mode === 'dark';
  return createTheme({
    palette: {
      mode,
      primary: { main: BRAND_PRIMARY, dark: BRAND_PRIMARY_DARK, light: BRAND_PRIMARY_LIGHT },
      secondary: { main: isDark ? '#9ca3af' : '#536471' },
      error: { main: '#dc2626' },
      warning: { main: '#d97706' },
      success: { main: '#059669' },
      info: { main: '#2563eb' },
      text: isDark
        ? { primary: '#e7e9ea', secondary: '#a8b3cf', disabled: '#5b6478' }
        : { primary: '#0f1419', secondary: '#536471', disabled: '#8899a6' },
      background: isDark
        ? { default: '#0f1419', paper: '#1a1f2e' }
        : { default: '#ffffff', paper: '#ffffff' },
      divider: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,25,0.08)',
    },
    typography: {
      fontFamily: '"Inter", "Noto Sans JP", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      h1: { fontSize: '36px', fontWeight: 600, letterSpacing: '-1.0px', lineHeight: 1.15 },
      h2: { fontSize: '28px', fontWeight: 600, letterSpacing: '-0.5px', lineHeight: 1.2 },
      h3: { fontSize: '22px', fontWeight: 600, letterSpacing: '-0.3px', lineHeight: 1.3 },
      h4: { fontSize: '18px', fontWeight: 600, letterSpacing: '-0.1px', lineHeight: 1.35 },
      h5: { fontSize: '16px', fontWeight: 600, letterSpacing: '0px', lineHeight: 1.4 },
      body1: { fontSize: '15px', fontWeight: 400, letterSpacing: '0px', lineHeight: 1.6 },
      body2: { fontSize: '13px', fontWeight: 400, letterSpacing: '0.1px', lineHeight: 1.5 },
      caption: { fontSize: '11px', fontWeight: 500, letterSpacing: '0.3px', lineHeight: 1.4 },
      button: { fontSize: '14px', fontWeight: 500, letterSpacing: '0px', textTransform: 'none' },
    },
    shape: { borderRadius: 8 },
    shadows: isDark
      ? [
          'none',
          '0 1px 2px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)',
          '0 2px 4px rgba(0,0,0,0.4), 0 4px 16px rgba(0,0,0,0.25)',
          '0 4px 12px rgba(0,0,0,0.5), 0 8px 32px rgba(0,0,0,0.3)',
          '0 8px 24px rgba(0,0,0,0.6), 0 16px 64px rgba(0,0,0,0.35)',
          ...Array(20).fill('none'),
        ]
      : [
          'none',
          '0 1px 2px rgba(15,20,25,0.04), 0 2px 8px rgba(15,20,25,0.02)',
          '0 2px 4px rgba(15,20,25,0.06), 0 4px 16px rgba(15,20,25,0.04)',
          '0 4px 12px rgba(15,20,25,0.08), 0 8px 32px rgba(15,20,25,0.04)',
          '0 8px 24px rgba(15,20,25,0.12), 0 16px 64px rgba(15,20,25,0.06)',
          ...Array(20).fill('none'),
        ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            padding: '10px 20px',
            transition: 'all 0.15s ease',
            '&:hover': { transform: 'scale(1.01)' },
            '&:active': { transform: 'scale(0.98)' },
          },
          contained: {
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          },
          outlined: {
            borderColor: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(15,20,25,0.15)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,25,0.08)'}`,
            boxShadow: isDark
              ? '0 1px 2px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2)'
              : '0 1px 2px rgba(15,20,25,0.04), 0 2px 8px rgba(15,20,25,0.02)',
            backgroundImage: 'none', // ★ MUI dark mode の自動オーバーレイを無効化 (純粋な paper 色を使う)
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#1a1f2e' : '#ffffff',
            color: isDark ? '#e7e9ea' : '#0f1419',
            boxShadow: 'none',
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,25,0.08)'}`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#4f46e5',
                boxShadow: isDark ? '0 0 0 3px rgba(79,70,229,0.25)' : '0 0 0 3px #eef2ff',
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 6, fontWeight: 500, fontSize: '12px' },
        },
      },
    },
  });
};

// 互換性のため デフォルト export は light テーマ
const theme = getTheme('light');
export default theme;
